import { memo, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/AddRounded';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import _map from 'lodash/map';
import _capitalize from 'lodash/capitalize';
// Local files
import { Root, StyledList } from './List.styled';
import PatientDialog from 'components/Dialogs/Patient/Patient';
import Search from 'components/Common/Search/Search';
import ListSkeleton from './Skeleton/Skeleton';
import Item from './Item/Item';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import usePatients from 'hooks/usePatients';
import useConversations from 'hooks/useConversations';
import useMessages from 'hooks/useMessages';
import { channels } from 'helpers/constants';
import { patientsSelectors } from 'features/patients';

const PatientsList = () => {
  const navigate = useNavigate();
  const { getPatients, clearLocalPatients, clearLocalActivePatient } = usePatients();
  const { clearLocalConversations, clearLocalActiveConversation } = useConversations();
  const { clearLocalMessages } = useMessages();
  const { data: patients, pagination } = useCustomSelector(patientsSelectors.getPatientsData);
  const fetching = useCustomSelector(patientsSelectors.patientsIsFetching);
  const [query, setQuery] = useState('');
  const [channel, setChannel] = useState('');
  const [patientDialog, setPatientDialog] = useState({ open: false });

  const fetchPatients = useCallback((offset, query, channel) => {
    getPatients({ offset, query, source: channel})
  }, [getPatients]);
  const handleScroll = ({ target: { scrollTop, scrollHeight, offsetHeight } }) =>
    scrollTop >= scrollHeight - offsetHeight && pagination.total_count > pagination.count + pagination.offset && !fetching && fetchPatients(pagination.limit + pagination.offset, query, channel);
  const handleCloseActivePatient = (e) => { 
    e.stopPropagation();
    clearLocalActivePatient();
    clearLocalMessages();
    clearLocalConversations();
    clearLocalActiveConversation();
    navigate('/patients');
  };
  const handleCreate = () => setPatientDialog({ open: true });
  const handleClose = () => setPatientDialog({ open: false });

  useEffect(() => {
    const timeout = setTimeout(() => {
      clearLocalPatients() 
      fetchPatients(0, query, channel)
    }, !!query ? 500 : 0);

    return () => {
      clearTimeout(timeout);
    }
  }, [query, channel, fetchPatients, clearLocalPatients]);
  useEffect(() => {
    return clearLocalPatients
  }, [clearLocalPatients]);

  return (
    <Root onClick={handleCloseActivePatient}>
      <PatientDialog {...patientDialog} onClose={handleClose} />
      <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center', p: '12px 16px 8px' }}>
        <Search fullWidth value={query} onChange={setQuery} />
        <Button
          variant='contained'
          color='primary'
          sx={{ minWidth: 'unset', height: 30, width: 30, p: .5, borderRadius: '50%' }}
          onClick={handleCreate}
        >
          <AddIcon />
        </Button>
      </Box>
      <TextField
        fullWidth
        margin='dense'
        select
        label='Filter by channel'
        value={channel}
        onChange={({ target: { value } }) => setChannel(value)}
        sx={{ width: '92%', mt: 1, mb: 1, ml: '4%', mr: '2%' }}
        InputProps={{
          endAdornment: (
            channel && (
              <IconButton
                size='small'
                onClick={() => setChannel('')}
                style={{ marginRight: 15 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          ),
        }}
      >
        {_map(channels, c => <MenuItem key={c} value={c}> {_capitalize(c)}</MenuItem>)}
      </TextField>
      <StyledList onScroll={handleScroll}>
        {_map(patients, ({ id, phone, updated_at }) =>
          <Item 
            key={id} 
            id={id}
            title={phone} 
            timestamp={new Date(updated_at).toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}
            userable={phone}
          />
        )}
        {fetching && <ListSkeleton />}
      </StyledList>
    </Root>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <PatientsList {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);